import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type {
  ReportChartSettingsV2,
  ReportExtras,
} from "@/common/report-configuration";
import type { SeriesConfig } from "@/common/type/report-types";
import type { ChartDataPoint, ReferenceLineInfo } from "@/types/chart";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import LegendWrapperV2 from "./components/legendWrapperV2";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EIGHT_TYPE_COLORS, lightenColor } from "@/common/colorUtils";
import { INSIGHTS_72_TYPE_LABELS } from "@/common/constants";

interface WheelDataPoint {
  type: string;
  typeLabel: string;
  Headcount: number;
  eightType: string;
  intensity: number;
  color: string;
}

interface InsightsWheelProps {
  data: ChartDataPoint[];
  reportExtras: ReportExtras;
  xAxis: string;
  seriesConfig: SeriesConfig;
  maxHeight?: number;
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
  chartSettings: ReportChartSettingsV2;
  xAxisLabel: string;
  yAxisLeftLabel: string;
  yAxisRightLabel: string;
  referenceLines?: ReferenceLineInfo[];
}

const TYPE_MAP: Record<string, string[]> = {
  REFORMER: [
    "116",
    "16",
    "1",
    "101",
    "136",
    "36",
    "21",
    "121",
    "156",
    "56",
    "41",
    "141",
  ],
  DIRECTOR: ["2", "3", "22", "23", "42", "43"],
  MOTIVATOR: [
    "104",
    "4",
    "5",
    "105",
    "124",
    "24",
    "25",
    "125",
    "144",
    "44",
    "45",
    "145",
  ],
  INSPIRER: ["6", "7", "26", "27", "46", "47"],
  HELPER: [
    "108",
    "8",
    "9",
    "109",
    "128",
    "28",
    "29",
    "129",
    "148",
    "48",
    "49",
    "149",
  ],
  SUPPORTER: ["10", "11", "30", "31", "50", "51"],
  COORDINATOR: [
    "112",
    "12",
    "13",
    "113",
    "132",
    "32",
    "33",
    "133",
    "152",
    "52",
    "53",
    "153",
  ],
  OBSERVER: ["14", "15", "34", "35", "54", "55"],
};

const MIN_COLOR_INTENSITY = 10;
const MAX_COLOR_INTENSITY = 100;

const getTypeColor = (type: string, intensity: number): string => {
  const baseColor = EIGHT_TYPE_COLORS[type];
  return lightenColor(baseColor, (100 - intensity) * 0.7);
};

const get8TypeFromCode = (code: string): string | undefined => {
  const type = Object.keys(TYPE_MAP).find((type) =>
    TYPE_MAP[type].includes(code),
  );
  return type;
};

const InsightsWheel: React.FC<InsightsWheelProps> = ({
  maxHeight = 400,
  data,
  chartSettings,
  seriesConfig,
  handleLegendItemChange,
}) => {
  const [hoveredCell, setHoveredCell] = useState<{
    code: string;
    x: number;
    y: number;
  } | null>(null);

  const wheelTypeData: Record<string, WheelDataPoint> = useMemo(() => {
    const dataMap: Record<string, WheelDataPoint> = {};
    data.forEach((item) => {
      const foundType = get8TypeFromCode(item.name.toString());
      if (!foundType) {
        return;
      }
      dataMap[item.name.toString()] = {
        type: item.name.toString(),
        typeLabel: INSIGHTS_72_TYPE_LABELS[item.name.toString()],
        Headcount: parseInt(item.Headcount as string, 10),
        eightType: foundType,
        intensity: 0,
        color: "#FFFFFF",
      };
    });
    // calculate the total headcount
    const totalHeadcount = Object.values(dataMap).reduce(
      (sum, item) => sum + (item.Headcount || 0),
      0,
    );
    let maxIntensity = 0;
    Object.values(dataMap).forEach((item) => {
      item.intensity = totalHeadcount
        ? (item.Headcount * 100.0) / totalHeadcount
        : 0;
      maxIntensity = Math.max(maxIntensity, item.intensity);
    });

    Object.values(dataMap).forEach((item) => {
      const colorIntensity = maxIntensity
        ? MIN_COLOR_INTENSITY +
          (item.intensity / maxIntensity) *
            (MAX_COLOR_INTENSITY - MIN_COLOR_INTENSITY)
        : MIN_COLOR_INTENSITY;
      item.color = getTypeColor(item.eightType, colorIntensity);
    });

    return dataMap;
  }, [data]);

  const legendPosition = chartSettings.legendPosition || "right";
  const legend = chartSettings.includeLegend ? (
    <LegendWrapperV2
      position={legendPosition}
      items={seriesConfig}
      onItemChange={handleLegendItemChange}
    />
  ) : null;

  const calculateFill = useCallback(
    (code: string, defaultFill: string) => {
      const item = wheelTypeData[code];
      if (!item) {
        return defaultFill;
      }
      if (item.intensity === 0) {
        return defaultFill;
      }
      return item.color;
    },
    [wheelTypeData],
  );

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.pointerType === "touch" || !(event.target instanceof Element)) {
      setHoveredCell(null);
      return;
    }

    const code = event.target
      .closest("path[data-cell]")
      ?.getAttribute("data-cell");
    setHoveredCell(code ? { code, x: event.clientX, y: event.clientY } : null);
  };

  const tooltipItem = hoveredCell ? wheelTypeData[hoveredCell.code] : undefined;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: ["top", "bottom"].includes(legendPosition)
          ? "column"
          : "row",
        alignItems: "center",
        width: "100%",
        height: maxHeight,
      }}
    >
      {["left", "top"].includes(legendPosition) && legend}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <TooltipProvider delayDuration={0}>
          <Tooltip
            open={hoveredCell !== null}
            onOpenChange={(open) => {
              if (!open) setHoveredCell(null);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 420 420"
              width="100%"
              height="100%"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label="Colour wheel with 8 types and 56 numbered cells"
              onPointerMove={handlePointerMove}
              onPointerLeave={() => setHoveredCell(null)}
              onPointerCancel={() => setHoveredCell(null)}
            >
              <defs>
                <path
                  id="tp-reformer"
                  d="M122.78 42.44A188.9 188.9 0 0 1 297.22 42.44"
                />
                <path
                  id="tp-director"
                  d="M297.22 42.44A188.9 188.9 0 0 1 377.56 122.78"
                />
                <path
                  id="tp-motivator"
                  d="M377.56 122.78A188.9 188.9 0 0 1 377.56 297.22"
                />
                <path
                  id="tp-inspirer"
                  d="M301.47 385.72A198.1 198.1 0 0 0 385.72 301.47"
                />
                <path
                  id="tp-helper"
                  d="M118.53 385.72A198.1 198.1 0 0 0 301.47 385.72"
                />
                <path
                  id="tp-supporter"
                  d="M34.28 301.47A198.1 198.1 0 0 0 118.53 385.72"
                />
                <path
                  id="tp-coordinator"
                  d="M42.44 297.22A188.9 188.9 0 0 1 42.44 122.78"
                />
                <path
                  id="tp-observer"
                  d="M42.44 122.78A188.9 188.9 0 0 1 122.78 42.44"
                />
              </defs>
              <style>{`
            .cell-label{font-family:Arial,Helvetica,sans-serif;font-size:8.5px;fill:#111;text-anchor:middle;dominant-baseline:central;pointer-events:none}
            .type-label{font-family:Arial,Helvetica,sans-serif}
            .ring{fill:none;stroke:#222}
            .spokes line{stroke:#222;stroke-width:.7;stroke-dasharray:3 2.5}
          `}</style>
              <circle
                cx="210"
                cy="210"
                r="206"
                fill="#fff"
                stroke="#bbb"
                strokeWidth=".5"
              />
              <g id="type-band">
                <path
                  id="type-reformer"
                  className="type-segment"
                  data-type="reformer"
                  d="M114.88 27.28A206 206 0 0 1 305.12 27.28L293.58 49.45A181 181 0 0 0 126.42 49.45Z"
                  fill="#A2559D"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-reformer" startOffset="50%">
                    REFORMER
                  </textPath>
                </text>
                <path
                  id="type-director"
                  className="type-segment"
                  data-type="director"
                  d="M305.12 27.28A206 206 0 0 1 392.72 114.88L370.55 126.42A181 181 0 0 0 293.58 49.45Z"
                  fill="#B73230"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-director" startOffset="50%">
                    DIRECTOR
                  </textPath>
                </text>
                <path
                  id="type-motivator"
                  className="type-segment"
                  data-type="motivator"
                  d="M392.72 114.88A206 206 0 0 1 392.72 305.12L370.55 293.58A181 181 0 0 0 370.55 126.42Z"
                  fill="#F07D00"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-motivator" startOffset="50%">
                    MOTIVATOR
                  </textPath>
                </text>
                <path
                  id="type-inspirer"
                  className="type-segment"
                  data-type="inspirer"
                  d="M392.72 305.12A206 206 0 0 1 305.12 392.72L293.58 370.55A181 181 0 0 0 370.55 293.58Z"
                  fill="#FDC94F"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-inspirer" startOffset="50%">
                    INSPIRER
                  </textPath>
                </text>
                <path
                  id="type-helper"
                  className="type-segment"
                  data-type="helper"
                  d="M305.12 392.72A206 206 0 0 1 114.88 392.72L126.42 370.55A181 181 0 0 0 293.58 370.55Z"
                  fill="#C7D41A"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-helper" startOffset="50%">
                    HELPER
                  </textPath>
                </text>
                <path
                  id="type-supporter"
                  className="type-segment"
                  data-type="supporter"
                  d="M114.88 392.72A206 206 0 0 1 27.28 305.12L49.45 293.58A181 181 0 0 0 126.42 370.55Z"
                  fill="#8AA60F"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-supporter" startOffset="50%">
                    SUPPORTER
                  </textPath>
                </text>
                <path
                  id="type-coordinator"
                  className="type-segment"
                  data-type="coordinator"
                  d="M27.28 305.12A206 206 0 0 1 27.28 114.88L49.45 126.42A181 181 0 0 0 49.45 293.58Z"
                  fill="#008A88"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-coordinator" startOffset="50%">
                    COORDINATOR
                  </textPath>
                </text>
                <path
                  id="type-observer"
                  className="type-segment"
                  data-type="observer"
                  d="M27.28 114.88A206 206 0 0 1 114.88 27.28L126.42 49.45A181 181 0 0 0 49.45 126.42Z"
                  fill="#0071BB"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  className="type-label"
                  fontSize="15"
                  fontWeight="700"
                  letterSpacing="0.6"
                  fill="#fff"
                  textAnchor="middle"
                >
                  <textPath href="#tp-observer" startOffset="50%">
                    OBSERVER
                  </textPath>
                </text>
              </g>
              <circle cx="210" cy="210" r="176" fill="#fff" />
              <g id="cells">
                <path
                  id="cell-1"
                  className="cell"
                  data-cell="1"
                  d="M210 34A176 176 0 0 1 262.92 42.15L246.08 95.55A120 120 0 0 0 210 90Z"
                  fill={calculateFill("1", "#fff")}
                />
                <path
                  id="cell-2"
                  className="cell"
                  data-cell="2"
                  d="M291.27 53.89A176 176 0 0 1 334.45 85.55L294.85 125.15A120 120 0 0 0 265.41 103.56Z"
                  fill={calculateFill("2", "#fff")}
                />
                <path
                  id="cell-3"
                  className="cell"
                  data-cell="3"
                  d="M334.45 85.55A176 176 0 0 1 366.11 128.73L316.44 154.59A120 120 0 0 0 294.85 125.15Z"
                  fill={calculateFill("3", "#fff")}
                />
                <path
                  id="cell-4"
                  className="cell"
                  data-cell="4"
                  d="M377.85 157.08A176 176 0 0 1 386 210L330 210A120 120 0 0 0 324.45 173.92Z"
                  fill={calculateFill("4", "#fff")}
                />
                <path
                  id="cell-5"
                  className="cell"
                  data-cell="5"
                  d="M386 210A176 176 0 0 1 377.85 262.92L324.45 246.08A120 120 0 0 0 330 210Z"
                  fill={calculateFill("5", "#fff")}
                />
                <path
                  id="cell-6"
                  className="cell"
                  data-cell="6"
                  d="M366.11 291.27A176 176 0 0 1 334.45 334.45L294.85 294.85A120 120 0 0 0 316.44 265.41Z"
                  fill={calculateFill("6", "#fff")}
                />
                <path
                  id="cell-7"
                  className="cell"
                  data-cell="7"
                  d="M334.45 334.45A176 176 0 0 1 291.27 366.11L265.41 316.44A120 120 0 0 0 294.85 294.85Z"
                  fill={calculateFill("7", "#fff")}
                />
                <path
                  id="cell-8"
                  className="cell"
                  data-cell="8"
                  d="M262.92 377.85A176 176 0 0 1 210 386L210 330A120 120 0 0 0 246.08 324.45Z"
                  fill={calculateFill("8", "#fff")}
                />
                <path
                  id="cell-9"
                  className="cell"
                  data-cell="9"
                  d="M210 386A176 176 0 0 1 157.08 377.85L173.92 324.45A120 120 0 0 0 210 330Z"
                  fill={calculateFill("9", "#fff")}
                />
                <path
                  id="cell-10"
                  className="cell"
                  data-cell="10"
                  d="M128.73 366.11A176 176 0 0 1 85.55 334.45L125.15 294.85A120 120 0 0 0 154.59 316.44Z"
                  fill={calculateFill("10", "#fff")}
                />
                <path
                  id="cell-11"
                  className="cell"
                  data-cell="11"
                  d="M85.55 334.45A176 176 0 0 1 53.89 291.27L103.56 265.41A120 120 0 0 0 125.15 294.85Z"
                  fill={calculateFill("11", "#fff")}
                />
                <path
                  id="cell-12"
                  className="cell"
                  data-cell="12"
                  d="M42.15 262.92A176 176 0 0 1 34 210L90 210A120 120 0 0 0 95.55 246.08Z"
                  fill={calculateFill("12", "#fff")}
                />
                <path
                  id="cell-13"
                  className="cell"
                  data-cell="13"
                  d="M34 210A176 176 0 0 1 42.15 157.08L95.55 173.92A120 120 0 0 0 90 210Z"
                  fill={calculateFill("13", "#fff")}
                />
                <path
                  id="cell-14"
                  className="cell"
                  data-cell="14"
                  d="M53.89 128.73A176 176 0 0 1 85.55 85.55L125.15 125.15A120 120 0 0 0 103.56 154.59Z"
                  fill={calculateFill("14", "#fff")}
                />
                <path
                  id="cell-15"
                  className="cell"
                  data-cell="15"
                  d="M85.55 85.55A176 176 0 0 1 128.73 53.89L154.59 103.56A120 120 0 0 0 125.15 125.15Z"
                  fill={calculateFill("15", "#fff")}
                />
                <path
                  id="cell-16"
                  className="cell"
                  data-cell="16"
                  d="M157.08 42.15A176 176 0 0 1 210 34L210 90A120 120 0 0 0 173.92 95.55Z"
                  fill={calculateFill("16", "#fff")}
                />
                <path
                  id="cell-21"
                  className="cell"
                  data-cell="21"
                  d="M210 90A120 120 0 0 1 246.08 95.55L233.46 135.61A78 78 0 0 0 210 132Z"
                  fill={calculateFill("21", "#fff")}
                />
                <path
                  id="cell-22"
                  className="cell"
                  data-cell="22"
                  d="M265.41 103.56A120 120 0 0 1 294.85 125.15L265.15 154.85A78 78 0 0 0 246.02 140.81Z"
                  fill={calculateFill("22", "#fff")}
                />
                <path
                  id="cell-23"
                  className="cell"
                  data-cell="23"
                  d="M294.85 125.15A120 120 0 0 1 316.44 154.59L279.19 173.98A78 78 0 0 0 265.15 154.85Z"
                  fill={calculateFill("23", "#fff")}
                />
                <path
                  id="cell-24"
                  className="cell"
                  data-cell="24"
                  d="M324.45 173.92A120 120 0 0 1 330 210L288 210A78 78 0 0 0 284.39 186.54Z"
                  fill={calculateFill("24", "#fff")}
                />
                <path
                  id="cell-25"
                  className="cell"
                  data-cell="25"
                  d="M330 210A120 120 0 0 1 324.45 246.08L284.39 233.46A78 78 0 0 0 288 210Z"
                  fill={calculateFill("25", "#fff")}
                />
                <path
                  id="cell-26"
                  className="cell"
                  data-cell="26"
                  d="M316.44 265.41A120 120 0 0 1 294.85 294.85L265.15 265.15A78 78 0 0 0 279.19 246.02Z"
                  fill={calculateFill("26", "#fff")}
                />
                <path
                  id="cell-27"
                  className="cell"
                  data-cell="27"
                  d="M294.85 294.85A120 120 0 0 1 265.41 316.44L246.02 279.19A78 78 0 0 0 265.15 265.15Z"
                  fill={calculateFill("27", "#fff")}
                />
                <path
                  id="cell-28"
                  className="cell"
                  data-cell="28"
                  d="M246.08 324.45A120 120 0 0 1 210 330L210 288A78 78 0 0 0 233.46 284.39Z"
                  fill={calculateFill("28", "#fff")}
                />
                <path
                  id="cell-29"
                  className="cell"
                  data-cell="29"
                  d="M210 330A120 120 0 0 1 173.92 324.45L186.54 284.39A78 78 0 0 0 210 288Z"
                  fill={calculateFill("29", "#fff")}
                />
                <path
                  id="cell-30"
                  className="cell"
                  data-cell="30"
                  d="M154.59 316.44A120 120 0 0 1 125.15 294.85L154.85 265.15A78 78 0 0 0 173.98 279.19Z"
                  fill={calculateFill("30", "#fff")}
                />
                <path
                  id="cell-31"
                  className="cell"
                  data-cell="31"
                  d="M125.15 294.85A120 120 0 0 1 103.56 265.41L140.81 246.02A78 78 0 0 0 154.85 265.15Z"
                  fill={calculateFill("31", "#fff")}
                />
                <path
                  id="cell-32"
                  className="cell"
                  data-cell="32"
                  d="M95.55 246.08A120 120 0 0 1 90 210L132 210A78 78 0 0 0 135.61 233.46Z"
                  fill={calculateFill("32", "#fff")}
                />
                <path
                  id="cell-33"
                  className="cell"
                  data-cell="33"
                  d="M90 210A120 120 0 0 1 95.55 173.92L135.61 186.54A78 78 0 0 0 132 210Z"
                  fill={calculateFill("33", "#fff")}
                />
                <path
                  id="cell-34"
                  className="cell"
                  data-cell="34"
                  d="M103.56 154.59A120 120 0 0 1 125.15 125.15L154.85 154.85A78 78 0 0 0 140.81 173.98Z"
                  fill={calculateFill("34", "#fff")}
                />
                <path
                  id="cell-35"
                  className="cell"
                  data-cell="35"
                  d="M125.15 125.15A120 120 0 0 1 154.59 103.56L173.98 140.81A78 78 0 0 0 154.85 154.85Z"
                  fill={calculateFill("35", "#fff")}
                />
                <path
                  id="cell-36"
                  className="cell"
                  data-cell="36"
                  d="M173.92 95.55A120 120 0 0 1 210 90L210 132A78 78 0 0 0 186.54 135.61Z"
                  fill={calculateFill("36", "#fff")}
                />
                <path
                  id="cell-41"
                  className="cell"
                  data-cell="41"
                  d="M210 132A78 78 0 0 1 233.46 135.61L221.43 173.76A38 38 0 0 0 210 172Z"
                  fill={calculateFill("41", "#fff")}
                />
                <path
                  id="cell-42"
                  className="cell"
                  data-cell="42"
                  d="M246.02 140.81A78 78 0 0 1 265.15 154.85L236.87 183.13A38 38 0 0 0 227.55 176.29Z"
                  fill={calculateFill("42", "#fff")}
                />
                <path
                  id="cell-43"
                  className="cell"
                  data-cell="43"
                  d="M265.15 154.85A78 78 0 0 1 279.19 173.98L243.71 192.45A38 38 0 0 0 236.87 183.13Z"
                  fill={calculateFill("43", "#fff")}
                />
                <path
                  id="cell-44"
                  className="cell"
                  data-cell="44"
                  d="M284.39 186.54A78 78 0 0 1 288 210L248 210A38 38 0 0 0 246.24 198.57Z"
                  fill={calculateFill("44", "#fff")}
                />
                <path
                  id="cell-45"
                  className="cell"
                  data-cell="45"
                  d="M288 210A78 78 0 0 1 284.39 233.46L246.24 221.43A38 38 0 0 0 248 210Z"
                  fill={calculateFill("45", "#fff")}
                />
                <path
                  id="cell-46"
                  className="cell"
                  data-cell="46"
                  d="M279.19 246.02A78 78 0 0 1 265.15 265.15L236.87 236.87A38 38 0 0 0 243.71 227.55Z"
                  fill={calculateFill("46", "#fff")}
                />
                <path
                  id="cell-47"
                  className="cell"
                  data-cell="47"
                  d="M265.15 265.15A78 78 0 0 1 246.02 279.19L227.55 243.71A38 38 0 0 0 236.87 236.87Z"
                  fill={calculateFill("47", "#fff")}
                />
                <path
                  id="cell-48"
                  className="cell"
                  data-cell="48"
                  d="M233.46 284.39A78 78 0 0 1 210 288L210 248A38 38 0 0 0 221.43 246.24Z"
                  fill={calculateFill("48", "#fff")}
                />
                <path
                  id="cell-49"
                  className="cell"
                  data-cell="49"
                  d="M210 288A78 78 0 0 1 186.54 284.39L198.57 246.24A38 38 0 0 0 210 248Z"
                  fill={calculateFill("49", "#fff")}
                />
                <path
                  id="cell-50"
                  className="cell"
                  data-cell="50"
                  d="M173.98 279.19A78 78 0 0 1 154.85 265.15L183.13 236.87A38 38 0 0 0 192.45 243.71Z"
                  fill={calculateFill("50", "#fff")}
                />
                <path
                  id="cell-51"
                  className="cell"
                  data-cell="51"
                  d="M154.85 265.15A78 78 0 0 1 140.81 246.02L176.29 227.55A38 38 0 0 0 183.13 236.87Z"
                  fill={calculateFill("51", "#fff")}
                />
                <path
                  id="cell-52"
                  className="cell"
                  data-cell="52"
                  d="M135.61 233.46A78 78 0 0 1 132 210L172 210A38 38 0 0 0 173.76 221.43Z"
                  fill={calculateFill("52", "#fff")}
                />
                <path
                  id="cell-53"
                  className="cell"
                  data-cell="53"
                  d="M132 210A78 78 0 0 1 135.61 186.54L173.76 198.57A38 38 0 0 0 172 210Z"
                  fill={calculateFill("53", "#fff")}
                />
                <path
                  id="cell-54"
                  className="cell"
                  data-cell="54"
                  d="M140.81 173.98A78 78 0 0 1 154.85 154.85L183.13 183.13A38 38 0 0 0 176.29 192.45Z"
                  fill={calculateFill("54", "#fff")}
                />
                <path
                  id="cell-55"
                  className="cell"
                  data-cell="55"
                  d="M154.85 154.85A78 78 0 0 1 173.98 140.81L192.45 176.29A38 38 0 0 0 183.13 183.13Z"
                  fill={calculateFill("55", "#fff")}
                />
                <path
                  id="cell-56"
                  className="cell"
                  data-cell="56"
                  d="M186.54 135.61A78 78 0 0 1 210 132L210 172A38 38 0 0 0 198.57 173.76Z"
                  fill={calculateFill("56", "#fff")}
                />
              </g>
              <g id="peak-cells">
                <path
                  id="cell-101"
                  className="cell cell-peak"
                  data-cell="101"
                  d="M262.92 42.15A176 176 0 0 1 291.27 53.89L265.41 103.56A120 120 0 0 0 246.08 95.55Z"
                  fill={calculateFill("101", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-104"
                  className="cell cell-peak"
                  data-cell="104"
                  d="M366.11 128.73A176 176 0 0 1 377.85 157.08L324.45 173.92A120 120 0 0 0 316.44 154.59Z"
                  fill={calculateFill("104", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-105"
                  className="cell cell-peak"
                  data-cell="105"
                  d="M377.85 262.92A176 176 0 0 1 366.11 291.27L316.44 265.41A120 120 0 0 0 324.45 246.08Z"
                  fill={calculateFill("105", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-108"
                  className="cell cell-peak"
                  data-cell="108"
                  d="M291.27 366.11A176 176 0 0 1 262.92 377.85L246.08 324.45A120 120 0 0 0 265.41 316.44Z"
                  fill={calculateFill("108", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-109"
                  className="cell cell-peak"
                  data-cell="109"
                  d="M157.08 377.85A176 176 0 0 1 128.73 366.11L154.59 316.44A120 120 0 0 0 173.92 324.45Z"
                  fill={calculateFill("109", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-112"
                  className="cell cell-peak"
                  data-cell="112"
                  d="M53.89 291.27A176 176 0 0 1 42.15 262.92L95.55 246.08A120 120 0 0 0 103.56 265.41Z"
                  fill={calculateFill("112", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-113"
                  className="cell cell-peak"
                  data-cell="113"
                  d="M42.15 157.08A176 176 0 0 1 53.89 128.73L103.56 154.59A120 120 0 0 0 95.55 173.92Z"
                  fill={calculateFill("113", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-116"
                  className="cell cell-peak"
                  data-cell="116"
                  d="M128.73 53.89A176 176 0 0 1 157.08 42.15L173.92 95.55A120 120 0 0 0 154.59 103.56Z"
                  fill={calculateFill("116", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-121"
                  className="cell cell-peak"
                  data-cell="121"
                  d="M246.08 95.55A120 120 0 0 1 265.41 103.56L246.02 140.81A78 78 0 0 0 233.46 135.61Z"
                  fill={calculateFill("121", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-124"
                  className="cell cell-peak"
                  data-cell="124"
                  d="M316.44 154.59A120 120 0 0 1 324.45 173.92L284.39 186.54A78 78 0 0 0 279.19 173.98Z"
                  fill={calculateFill("124", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-125"
                  className="cell cell-peak"
                  data-cell="125"
                  d="M324.45 246.08A120 120 0 0 1 316.44 265.41L279.19 246.02A78 78 0 0 0 284.39 233.46Z"
                  fill={calculateFill("125", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-128"
                  className="cell cell-peak"
                  data-cell="128"
                  d="M265.41 316.44A120 120 0 0 1 246.08 324.45L233.46 284.39A78 78 0 0 0 246.02 279.19Z"
                  fill={calculateFill("128", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-129"
                  className="cell cell-peak"
                  data-cell="129"
                  d="M173.92 324.45A120 120 0 0 1 154.59 316.44L173.98 279.19A78 78 0 0 0 186.54 284.39Z"
                  fill={calculateFill("129", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-132"
                  className="cell cell-peak"
                  data-cell="132"
                  d="M103.56 265.41A120 120 0 0 1 95.55 246.08L135.61 233.46A78 78 0 0 0 140.81 246.02Z"
                  fill={calculateFill("132", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-133"
                  className="cell cell-peak"
                  data-cell="133"
                  d="M95.55 173.92A120 120 0 0 1 103.56 154.59L140.81 173.98A78 78 0 0 0 135.61 186.54Z"
                  fill={calculateFill("133", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-136"
                  className="cell cell-peak"
                  data-cell="136"
                  d="M154.59 103.56A120 120 0 0 1 173.92 95.55L186.54 135.61A78 78 0 0 0 173.98 140.81Z"
                  fill={calculateFill("136", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-141"
                  className="cell cell-peak"
                  data-cell="141"
                  d="M233.46 135.61A78 78 0 0 1 246.02 140.81L227.55 176.29A38 38 0 0 0 221.43 173.76Z"
                  fill={calculateFill("141", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-144"
                  className="cell cell-peak"
                  data-cell="144"
                  d="M279.19 173.98A78 78 0 0 1 284.39 186.54L246.24 198.57A38 38 0 0 0 243.71 192.45Z"
                  fill={calculateFill("144", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-145"
                  className="cell cell-peak"
                  data-cell="145"
                  d="M284.39 233.46A78 78 0 0 1 279.19 246.02L243.71 227.55A38 38 0 0 0 246.24 221.43Z"
                  fill={calculateFill("145", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-148"
                  className="cell cell-peak"
                  data-cell="148"
                  d="M246.02 279.19A78 78 0 0 1 233.46 284.39L221.43 246.24A38 38 0 0 0 227.55 243.71Z"
                  fill={calculateFill("148", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-149"
                  className="cell cell-peak"
                  data-cell="149"
                  d="M186.54 284.39A78 78 0 0 1 173.98 279.19L192.45 243.71A38 38 0 0 0 198.57 246.24Z"
                  fill={calculateFill("149", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-152"
                  className="cell cell-peak"
                  data-cell="152"
                  d="M140.81 246.02A78 78 0 0 1 135.61 233.46L173.76 221.43A38 38 0 0 0 176.29 227.55Z"
                  fill={calculateFill("152", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-153"
                  className="cell cell-peak"
                  data-cell="153"
                  d="M135.61 186.54A78 78 0 0 1 140.81 173.98L176.29 192.45A38 38 0 0 0 173.76 198.57Z"
                  fill={calculateFill("153", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
                <path
                  id="cell-156"
                  className="cell cell-peak"
                  data-cell="156"
                  d="M173.98 140.81A78 78 0 0 1 186.54 135.61L198.57 173.76A38 38 0 0 0 192.45 176.29Z"
                  fill={calculateFill("156", "#EEEEEE")}
                  stroke="#222"
                  strokeWidth="0.5"
                />
              </g>
              <g className="spokes" id="spokes" pointerEvents="none">
                <line x1="210" y1="172" x2="210" y2="34" />
                <line x1="236.87" y1="183.13" x2="334.45" y2="85.55" />
                <line x1="248" y1="210" x2="386" y2="210" />
                <line x1="236.87" y1="236.87" x2="334.45" y2="334.45" />
                <line x1="210" y1="248" x2="210" y2="386" />
                <line x1="183.13" y1="236.87" x2="85.55" y2="334.45" />
                <line x1="172" y1="210" x2="34" y2="210" />
                <line x1="183.13" y1="183.13" x2="85.55" y2="85.55" />
              </g>
              <g id="rings" pointerEvents="none">
                <circle
                  className="ring"
                  cx="210"
                  cy="210"
                  r="176"
                  strokeWidth="1.4"
                />
                <circle
                  className="ring"
                  cx="210"
                  cy="210"
                  r="120"
                  strokeWidth="1"
                />
                <circle
                  className="ring"
                  cx="210"
                  cy="210"
                  r="78"
                  stroke="#FF0000"
                  strokeWidth="1"
                />
                <circle
                  className="ring"
                  cx="210"
                  cy="210"
                  r="38"
                  strokeWidth="1"
                />
                <circle
                  className="ring"
                  cx="210"
                  cy="210"
                  r="29"
                  strokeWidth="1"
                  fill="#fff"
                />
              </g>
              {/* <g id="cell-labels">
                <text
                  id="label-1"
                  className="cell-label"
                  data-cell="1"
                  x="232.36"
                  y="64.71"
                >
                  1
                </text>
                <text
                  id="label-2"
                  className="cell-label"
                  data-cell="2"
                  x="296.92"
                  y="91.45"
                >
                  2
                </text>
                <text
                  id="label-3"
                  className="cell-label"
                  data-cell="3"
                  x="328.55"
                  y="123.08"
                >
                  3
                </text>
                <text
                  id="label-4"
                  className="cell-label"
                  data-cell="4"
                  x="355.29"
                  y="187.64"
                >
                  4
                </text>
                <text
                  id="label-5"
                  className="cell-label"
                  data-cell="5"
                  x="355.29"
                  y="232.36"
                >
                  5
                </text>
                <text
                  id="label-6"
                  className="cell-label"
                  data-cell="6"
                  x="328.55"
                  y="296.92"
                >
                  6
                </text>
                <text
                  id="label-7"
                  className="cell-label"
                  data-cell="7"
                  x="296.92"
                  y="328.55"
                >
                  7
                </text>
                <text
                  id="label-8"
                  className="cell-label"
                  data-cell="8"
                  x="232.36"
                  y="355.29"
                >
                  8
                </text>
                <text
                  id="label-9"
                  className="cell-label"
                  data-cell="9"
                  x="187.64"
                  y="355.29"
                >
                  9
                </text>
                <text
                  id="label-10"
                  className="cell-label"
                  data-cell="10"
                  x="123.08"
                  y="328.55"
                >
                  10
                </text>
                <text
                  id="label-11"
                  className="cell-label"
                  data-cell="11"
                  x="91.45"
                  y="296.92"
                >
                  11
                </text>
                <text
                  id="label-12"
                  className="cell-label"
                  data-cell="12"
                  x="64.71"
                  y="232.36"
                >
                  12
                </text>
                <text
                  id="label-13"
                  className="cell-label"
                  data-cell="13"
                  x="64.71"
                  y="187.64"
                >
                  13
                </text>
                <text
                  id="label-14"
                  className="cell-label"
                  data-cell="14"
                  x="91.45"
                  y="123.08"
                >
                  14
                </text>
                <text
                  id="label-15"
                  className="cell-label"
                  data-cell="15"
                  x="123.08"
                  y="91.45"
                >
                  15
                </text>
                <text
                  id="label-16"
                  className="cell-label"
                  data-cell="16"
                  x="187.64"
                  y="64.71"
                >
                  16
                </text>
                <text
                  id="label-21"
                  className="cell-label"
                  data-cell="21"
                  x="225.06"
                  y="112.15"
                >
                  21
                </text>
                <text
                  id="label-22"
                  className="cell-label"
                  data-cell="22"
                  x="268.54"
                  y="130.16"
                >
                  22
                </text>
                <text
                  id="label-23"
                  className="cell-label"
                  data-cell="23"
                  x="289.84"
                  y="151.46"
                >
                  23
                </text>
                <text
                  id="label-24"
                  className="cell-label"
                  data-cell="24"
                  x="307.85"
                  y="194.94"
                >
                  24
                </text>
                <text
                  id="label-25"
                  className="cell-label"
                  data-cell="25"
                  x="307.85"
                  y="225.06"
                >
                  25
                </text>
                <text
                  id="label-26"
                  className="cell-label"
                  data-cell="26"
                  x="289.84"
                  y="268.54"
                >
                  26
                </text>
                <text
                  id="label-27"
                  className="cell-label"
                  data-cell="27"
                  x="268.54"
                  y="289.84"
                >
                  27
                </text>
                <text
                  id="label-28"
                  className="cell-label"
                  data-cell="28"
                  x="225.06"
                  y="307.85"
                >
                  28
                </text>
                <text
                  id="label-29"
                  className="cell-label"
                  data-cell="29"
                  x="194.94"
                  y="307.85"
                >
                  29
                </text>
                <text
                  id="label-30"
                  className="cell-label"
                  data-cell="30"
                  x="151.46"
                  y="289.84"
                >
                  30
                </text>
                <text
                  id="label-31"
                  className="cell-label"
                  data-cell="31"
                  x="130.16"
                  y="268.54"
                >
                  31
                </text>
                <text
                  id="label-32"
                  className="cell-label"
                  data-cell="32"
                  x="112.15"
                  y="225.06"
                >
                  32
                </text>
                <text
                  id="label-33"
                  className="cell-label"
                  data-cell="33"
                  x="112.15"
                  y="194.94"
                >
                  33
                </text>
                <text
                  id="label-34"
                  className="cell-label"
                  data-cell="34"
                  x="130.16"
                  y="151.46"
                >
                  34
                </text>
                <text
                  id="label-35"
                  className="cell-label"
                  data-cell="35"
                  x="151.46"
                  y="130.16"
                >
                  35
                </text>
                <text
                  id="label-36"
                  className="cell-label"
                  data-cell="36"
                  x="194.94"
                  y="112.15"
                >
                  36
                </text>
                <text
                  id="label-41"
                  className="cell-label"
                  data-cell="41"
                  x="218.82"
                  y="152.68"
                >
                  41
                </text>
                <text
                  id="label-42"
                  className="cell-label"
                  data-cell="42"
                  x="244.3"
                  y="163.23"
                >
                  42
                </text>
                <text
                  id="label-43"
                  className="cell-label"
                  data-cell="43"
                  x="256.77"
                  y="175.7"
                >
                  43
                </text>
                <text
                  id="label-44"
                  className="cell-label"
                  data-cell="44"
                  x="267.32"
                  y="201.18"
                >
                  44
                </text>
                <text
                  id="label-45"
                  className="cell-label"
                  data-cell="45"
                  x="267.32"
                  y="218.82"
                >
                  45
                </text>
                <text
                  id="label-46"
                  className="cell-label"
                  data-cell="46"
                  x="256.77"
                  y="244.3"
                >
                  46
                </text>
                <text
                  id="label-47"
                  className="cell-label"
                  data-cell="47"
                  x="244.3"
                  y="256.77"
                >
                  47
                </text>
                <text
                  id="label-48"
                  className="cell-label"
                  data-cell="48"
                  x="218.82"
                  y="267.32"
                >
                  48
                </text>
                <text
                  id="label-49"
                  className="cell-label"
                  data-cell="49"
                  x="201.18"
                  y="267.32"
                >
                  49
                </text>
                <text
                  id="label-50"
                  className="cell-label"
                  data-cell="50"
                  x="175.7"
                  y="256.77"
                >
                  50
                </text>
                <text
                  id="label-51"
                  className="cell-label"
                  data-cell="51"
                  x="163.23"
                  y="244.3"
                >
                  51
                </text>
                <text
                  id="label-52"
                  className="cell-label"
                  data-cell="52"
                  x="152.68"
                  y="218.82"
                >
                  52
                </text>
                <text
                  id="label-53"
                  className="cell-label"
                  data-cell="53"
                  x="152.68"
                  y="201.18"
                >
                  53
                </text>
                <text
                  id="label-54"
                  className="cell-label"
                  data-cell="54"
                  x="163.23"
                  y="175.7"
                >
                  54
                </text>
                <text
                  id="label-55"
                  className="cell-label"
                  data-cell="55"
                  x="175.7"
                  y="163.23"
                >
                  55
                </text>
                <text
                  id="label-56"
                  className="cell-label"
                  data-cell="56"
                  x="201.18"
                  y="152.68"
                >
                  56
                </text>
                <text
                  id="label-101"
                  className="cell-label"
                  data-cell="101"
                  x="266.25"
                  y="74.19"
                >
                  101
                </text>
                <text
                  id="label-104"
                  className="cell-label"
                  data-cell="104"
                  x="345.81"
                  y="153.75"
                >
                  104
                </text>
                <text
                  id="label-105"
                  className="cell-label"
                  data-cell="105"
                  x="345.81"
                  y="266.25"
                >
                  105
                </text>
                <text
                  id="label-108"
                  className="cell-label"
                  data-cell="108"
                  x="266.25"
                  y="345.81"
                >
                  108
                </text>
                <text
                  id="label-109"
                  className="cell-label"
                  data-cell="109"
                  x="153.75"
                  y="345.81"
                >
                  109
                </text>
                <text
                  id="label-112"
                  className="cell-label"
                  data-cell="112"
                  x="74.19"
                  y="266.25"
                >
                  112
                </text>
                <text
                  id="label-113"
                  className="cell-label"
                  data-cell="113"
                  x="74.19"
                  y="153.75"
                >
                  113
                </text>
                <text
                  id="label-116"
                  className="cell-label"
                  data-cell="116"
                  x="153.75"
                  y="74.19"
                >
                  116
                </text>
                <text
                  id="label-121"
                  className="cell-label"
                  data-cell="121"
                  x="247.89"
                  y="118.54"
                >
                  121
                </text>
                <text
                  id="label-124"
                  className="cell-label"
                  data-cell="124"
                  x="301.46"
                  y="172.11"
                >
                  124
                </text>
                <text
                  id="label-125"
                  className="cell-label"
                  data-cell="125"
                  x="301.46"
                  y="247.89"
                >
                  125
                </text>
                <text
                  id="label-128"
                  className="cell-label"
                  data-cell="128"
                  x="247.89"
                  y="301.46"
                >
                  128
                </text>
                <text
                  id="label-129"
                  className="cell-label"
                  data-cell="129"
                  x="172.11"
                  y="301.46"
                >
                  129
                </text>
                <text
                  id="label-132"
                  className="cell-label"
                  data-cell="132"
                  x="118.54"
                  y="247.89"
                >
                  132
                </text>
                <text
                  id="label-133"
                  className="cell-label"
                  data-cell="133"
                  x="118.54"
                  y="172.11"
                >
                  133
                </text>
                <text
                  id="label-136"
                  className="cell-label"
                  data-cell="136"
                  x="172.11"
                  y="118.54"
                >
                  136
                </text>
                <text
                  id="label-141"
                  className="cell-label"
                  data-cell="141"
                  x="232.2"
                  y="156.41"
                >
                  141
                </text>
                <text
                  id="label-144"
                  className="cell-label"
                  data-cell="144"
                  x="263.59"
                  y="187.8"
                >
                  144
                </text>
                <text
                  id="label-145"
                  className="cell-label"
                  data-cell="145"
                  x="263.59"
                  y="232.2"
                >
                  145
                </text>
                <text
                  id="label-148"
                  className="cell-label"
                  data-cell="148"
                  x="232.2"
                  y="263.59"
                >
                  148
                </text>
                <text
                  id="label-149"
                  className="cell-label"
                  data-cell="149"
                  x="187.8"
                  y="263.59"
                >
                  149
                </text>
                <text
                  id="label-152"
                  className="cell-label"
                  data-cell="152"
                  x="156.41"
                  y="232.2"
                >
                  152
                </text>
                <text
                  id="label-153"
                  className="cell-label"
                  data-cell="153"
                  x="156.41"
                  y="187.8"
                >
                  153
                </text>
                <text
                  id="label-156"
                  className="cell-label"
                  data-cell="156"
                  x="187.8"
                  y="156.41"
                >
                  156
                </text>
              </g> */}
              <g id="center">
                <path d="M210 210V190A20 20 0 0 0 190 210Z" fill="#0071BB" />
                <path d="M210 210V190A20 20 0 0 1 230 210Z" fill="#B73230" />
                <path d="M210 210H230A20 20 0 0 1 210 230Z" fill="#FDC94F" />
                <path d="M210 210V230A20 20 0 0 1 190 210Z" fill="#8AA60F" />
                <circle
                  cx="210"
                  cy="210"
                  r="20"
                  fill="none"
                  stroke="#222"
                  strokeWidth=".8"
                />
              </g>
            </svg>
            {hoveredCell &&
              createPortal(
                <>
                  <TooltipTrigger asChild>
                    <span
                      aria-hidden="true"
                      tabIndex={-1}
                      style={{
                        position: "fixed",
                        left: hoveredCell.x,
                        top: hoveredCell.y,
                        width: 1,
                        height: 1,
                        pointerEvents: "none",
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    align="start"
                    sideOffset={12}
                    collisionPadding={8}
                    updatePositionStrategy="always"
                    className="pointer-events-none"
                  >
                    <p className="font-semibold">
                      {tooltipItem?.typeLabel ??
                        INSIGHTS_72_TYPE_LABELS[hoveredCell.code]}
                    </p>
                    <p>
                      {(tooltipItem?.Headcount ?? 0).toLocaleString("en-US")}{" "}
                      employee{(tooltipItem?.Headcount ?? 0) === 1 ? "" : "s"}
                    </p>
                    <p>{(tooltipItem?.intensity ?? 0).toFixed(1)}% of total</p>
                  </TooltipContent>
                </>,
                document.body,
              )}
          </Tooltip>
        </TooltipProvider>
      </div>
      {["right", "bottom"].includes(legendPosition) && legend}
    </div>
  );
};

export default InsightsWheel;
